import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { HPC_COLORS } from "./hpcTheme";
import { fetchMembershipQuote, formatLkr } from "./hpcMembershipPricing";

export const REAPPLY_MESSAGE = "Please reapply your promotion code.";
export const ONE_PROMOTION_MESSAGE = "Only one promotion can be applied per registration.";

const quoteKey = (membershipType, members, nicPassport) =>
  JSON.stringify({
    membershipType,
    members: (members || []).map((m) => [m.fullName || "", m.dateOfBirth || ""]),
    nicPassport: nicPassport || "",
  });

// The registration form always uses the dark HPU theme, so the brand colours are used here.
const SUCCESS_GREEN = "#4cceac";
const ERROR_RED = "#ff6b6b";

const fieldSx = {
  flex: "1 1 220px",
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    color: HPC_COLORS.white,
    bgcolor: "rgba(0,0,0,0.25)",
    "& fieldset": { borderColor: "rgba(184, 197, 214, 0.55)" },
    "&:hover fieldset": { borderColor: HPC_COLORS.silver },
    "&.Mui-focused fieldset": { borderColor: HPC_COLORS.hpuOrange, borderWidth: 2 },
  },
  "& .MuiInputLabel-root": { color: HPC_COLORS.silver },
  "& .MuiInputLabel-root.Mui-focused": { color: HPC_COLORS.hpuOrange },
  "& .MuiInputBase-input": { fontWeight: 700, letterSpacing: "0.08em" },
};

const applyButtonSx = {
  minHeight: 40,
  px: 3,
  fontWeight: 800,
  flex: { xs: "1 1 100%", sm: "0 0 auto" },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.6)",
    backgroundColor: "rgba(255,255,255,0.12)",
  },
};

const removeButtonSx = {
  minHeight: 40,
  px: 3,
  fontWeight: 700,
  color: ERROR_RED,
  borderColor: ERROR_RED,
  borderWidth: 2,
  flex: { xs: "1 1 100%", sm: "0 0 auto" },
  "&:hover": { borderColor: ERROR_RED, borderWidth: 2, backgroundColor: "rgba(255, 107, 107, 0.12)" },
};

const BreakdownRow = ({ label, value, strong, accent }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.5, flexWrap: "wrap" }}>
    <Typography variant="body2" sx={{ color: HPC_COLORS.silver }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: strong ? 800 : 600, color: accent || HPC_COLORS.white, textAlign: "right" }}
    >
      {value}
    </Typography>
  </Box>
);

export const PromotionBreakdown = ({ pricing }) => {
  if (!pricing) return null;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mt: 1.5,
        bgcolor: "rgba(0,0,0,0.3)",
        borderColor: "rgba(184, 197, 214, 0.35)",
        borderLeft: `4px solid ${HPC_COLORS.hpuOrange}`,
      }}
    >
      {pricing.no_extra_saving ? (
        <BreakdownRow label="Normal price" value={formatLkr(pricing.base_total)} />
      ) : (
        <>
          <BreakdownRow
            label={pricing.promotion?.promotion_type === "ADMISSION_WAIVER" ? "Normal price" : "Original value"}
            value={formatLkr(pricing.promotion_base_value ?? pricing.original_value)}
          />
          <BreakdownRow
            label={`Promotion (${pricing.promotion?.code || "code"})`}
            value={`− ${formatLkr(pricing.discount_amount)}`}
            accent={SUCCESS_GREEN}
          />
        </>
      )}
      <BreakdownRow label="You pay" value={formatLkr(pricing.final_total)} strong accent={HPC_COLORS.hpuOrange} />
      {!pricing.no_extra_saving && pricing.promotion?.promotion_type !== "ADMISSION_WAIVER" && (
        <BreakdownRow label="Normal price" value={formatLkr(pricing.base_total)} />
      )}
    </Paper>
  );
};

/**
 * Promotion code entry for the main member. Prices come only from the server quote.
 * ``members`` is the main member followed by family members (used for Family prices).
 */
const PromotionCodeField = ({
  membershipType,
  members,
  nicPassport,
  appliedCode,
  appliedPricing,
  onApplied,
  onRemoved,
}) => {
  const [input, setInput] = useState(appliedCode || "");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const appliedKeyRef = useRef(null);
  const callbacksRef = useRef({ onApplied, onRemoved });
  callbacksRef.current = { onApplied, onRemoved };

  const key = quoteKey(membershipType, members, nicPassport);
  const missingDob = (members || []).some((m) => !m.dateOfBirth);

  const runQuote = async (code) =>
    fetchMembershipQuote({ membershipType, members, promoCode: code, nicPassport });

  const apply = async () => {
    const code = input.trim();
    if (!code) {
      setError("Enter a promotion code.");
      return;
    }
    if (appliedCode || /[\s,;]/.test(code)) {
      setError(ONE_PROMOTION_MESSAGE);
      return;
    }
    setChecking(true);
    setError(null);
    setNotice(null);
    try {
      const data = await runQuote(code);
      if (data.promotion?.valid) {
        appliedKeyRef.current = key;
        setInput(data.promotion.code);
        setNotice(data.promotion.message);
        onApplied(data.promotion.code, data.pricing);
      } else {
        setError(data.promotion?.message || "Invalid promotion code. Please check and try again.");
      }
    } catch (err) {
      setError(
        err.response?.data?.promotion?.message ||
          err.response?.data?.error ||
          "We could not check this code right now. Please try again.",
      );
    } finally {
      setChecking(false);
    }
  };

  const remove = () => {
    appliedKeyRef.current = null;
    setInput("");
    setNotice(null);
    setError(null);
    onRemoved(null);
  };

  // Package, duration, members or dates of birth changed: re-check the applied code.
  useEffect(() => {
    if (!appliedCode || !membershipType || missingDob) return undefined;
    if (appliedKeyRef.current === key) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await runQuote(appliedCode);
        if (cancelled) return;
        appliedKeyRef.current = key;
        if (data.promotion?.valid) {
          callbacksRef.current.onApplied(data.promotion.code, data.pricing);
        } else {
          appliedKeyRef.current = null;
          setInput("");
          setNotice(null);
          setError(REAPPLY_MESSAGE);
          callbacksRef.current.onRemoved(REAPPLY_MESSAGE);
        }
      } catch {
        if (!cancelled) setError("We could not re-check your promotion code. Please try again.");
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // key captures every input the quote depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, appliedCode, membershipType, missingDob]);

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: HPC_COLORS.silver }}>
        Promotion code
      </Typography>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
        <TextField
          size="small"
          label="Promotion code (optional)"
          value={input}
          onChange={(e) => {
            setInput(e.target.value.toUpperCase());
            setError(null);
          }}
          disabled={checking}
          error={Boolean(error)}
          inputProps={{
            maxLength: 32,
            autoCapitalize: "characters",
            readOnly: Boolean(appliedCode),
          }}
          InputProps={
            appliedCode
              ? { endAdornment: <CheckCircleOutlineIcon sx={{ color: SUCCESS_GREEN }} fontSize="small" /> }
              : undefined
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" && !appliedCode) {
              e.preventDefault();
              apply();
            }
          }}
          sx={fieldSx}
        />
        {appliedCode ? (
          <Button variant="outlined" onClick={remove} startIcon={<CloseIcon />} sx={removeButtonSx}>
            Remove
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={apply}
            disabled={checking || !membershipType || missingDob || !input.trim()}
            sx={applyButtonSx}
            startIcon={checking ? <CircularProgress size={16} color="inherit" /> : <LocalOfferOutlinedIcon />}
          >
            Apply
          </Button>
        )}
      </Box>
      {missingDob && !appliedCode && (
        <Typography variant="caption" display="block" sx={{ color: HPC_COLORS.silver, mt: 0.5 }}>
          Enter every family member's date of birth before applying a code.
        </Typography>
      )}
      {error && (
        <Alert severity="error" variant="filled" sx={{ mt: 1, fontWeight: 600 }}>
          {error}
        </Alert>
      )}
      {appliedCode && notice && (
        appliedPricing?.no_extra_saving ? (
          <Alert severity="info" variant="filled" sx={{ mt: 1, fontWeight: 600 }}>
            No extra saving for this package
          </Alert>
        ) : (
          <Alert
            severity="success"
            variant="filled"
            sx={{ mt: 1, fontWeight: 600, bgcolor: "#2e7d32", color: HPC_COLORS.white }}
          >
            {notice}
          </Alert>
        )
      )}
      {appliedCode && appliedPricing?.promotion?.terms && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: HPC_COLORS.silver }}>
          {appliedPricing.promotion.terms}
        </Typography>
      )}
      {appliedCode && <PromotionBreakdown pricing={appliedPricing} />}
    </Box>
  );
};

export default PromotionCodeField;
